import React from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from '@mui/material';
import { COLORS } from '../../../Components/colors';

const FONT = {
  label: '11px',
  value: '11px',
  title: '1.1rem'
};

const ROW_HEIGHT = 32;

const LEFT_COL_WIDTHS = ['50%', '50%'];

const cell = {
  border: `1px solid ${COLORS.borderTable}`,
  padding: '4px 8px',
  whiteSpace: 'nowrap',
  fontSize: FONT.label,
  fontFamily: '"Poppins", sans-serif'
};

const OTBReportHeader = ({ reportData, pickupDaysLabel, displaySettings, selectedRows, data, showLegalName }) => {
  const fromDate = reportData?.ShortFromDate ? dayjs(reportData.ShortFromDate).format('MMMM DD, YYYY') : '';
  const toDate = reportData?.ShortToDate ? dayjs(reportData.ShortToDate).format('MMMM DD, YYYY') : '';
  const pickupDays = pickupDaysLabel || '';

  let corpName = '-';
  let profitCenter = '-';
  let brandName = '-';

  if (data && data.length > 0) {
    const getUniqueValues = (key) => [
      ...new Set(
        data
          .map(item => item[key])
          .filter(value => value !== null && value !== undefined && value !== '')
      )
    ];

    const corpValues = getUniqueValues(
      showLegalName ? 'CorpLegalName' : 'Corpname'
    );

    const pcValues = getUniqueValues('PCName');
    const brandValues = getUniqueValues('BrandName');

    corpName =
      corpValues.length > 1 ? 'Multiple' : corpValues[0] || '-';

    profitCenter =
      pcValues.length > 1 ? 'Multiple' : pcValues[0] || '-';

    brandName =
      brandValues.length > 1 ? 'Multiple' : brandValues[0] || '-';
  }

  const formatText = (text) => {
    if (!text || typeof text !== 'string') return text;
    if (displaySettings?.textFormat === 'upper') return text.toUpperCase();
    if (displaySettings?.textFormat === 'lower') return text.toLowerCase();
    return text;
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(300px, 360px) 1fr auto',
        alignItems: 'stretch',
        width: '100%'
      }}
    >
      {/* LEFT TABLE */}
      <TableContainer>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableBody>
            {[
              ['Corporation/Legal Name', corpName],
              ['Profit Center', profitCenter],
              ['Brand Name', brandName]
            ].map((row, i) => (
              <TableRow key={i} sx={{ height: ROW_HEIGHT }}>
                <TableCell sx={{ ...cell, width: LEFT_COL_WIDTHS[0], color: COLORS.textBlack }}>
                  {formatText(row[0])}
                </TableCell>
                <TableCell
                  sx={{
                    ...cell,
                    width: LEFT_COL_WIDTHS[1],
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 1 // Required for ellipsis to work in table-cell
                  }}
                  title={formatText(row[1])}
                >
                  {formatText(row[1])}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* CENTER TITLE */}
      <Box
        sx={{
          border: `1px solid ${COLORS.borderTable}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography sx={{ fontSize: FONT.title, fontWeight: 700 }}>
          {formatText('OTB Report')}
        </Typography>
      </Box>

      {/* RIGHT TABLE */}
      <TableContainer sx={{ width: 'auto' }}>
        <Table size="small" sx={{ tableLayout: 'auto', width: '100%', height: '100%' }}>
          <TableBody>
            <TableRow sx={{ height: (ROW_HEIGHT * 3) / 2 }}>
              <TableCell sx={{ ...cell, color: COLORS.textBlack }}>
                {formatText('Date')}
              </TableCell>
              <TableCell sx={{ ...cell, fontWeight: 600, color: COLORS.textBlack }}>
                {formatText(fromDate)}
              </TableCell>
              <TableCell sx={{ ...cell, color: COLORS.textBlack, textAlign: 'center' }}>
                {formatText('To')}
              </TableCell>
              <TableCell sx={{ ...cell, fontWeight: 600, color: COLORS.textBlack }}>
                {formatText(toDate)}
              </TableCell>
            </TableRow>
            <TableRow sx={{ height: (ROW_HEIGHT * 3) / 2 }}>
              <TableCell sx={{ ...cell, color: COLORS.textBlack }}>
                {formatText('Pickup Days')}
              </TableCell>
              <TableCell colSpan={3} sx={{ ...cell, fontWeight: 600, color: COLORS.textBlack }}>
                {formatText(pickupDays)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default React.memo(OTBReportHeader);
